import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { offlineDb, scopedKey } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

/**
 * What to report when the server call failed AND the phone has no saved copy. A real answer from the server (401 signed out, 403, 404, 5xx)
 * is passed on as it is: the old message "No cached copy available" hid it (2026-10-02: a dead session showed up as that message, so the real
 * cause was invisible). Only when there was no answer at all (offline, timeout) is it the "no cached copy" error, which keeps the original as its cause.
 */
/**
 * Should a failed fetch fall back to the phone's saved copy? Yes when there was no answer at all (offline, timeout) or the server itself failed (5xx).
 * No when the server ANSWERED that the request is not allowed or not found (401 signed out, 403, 404, 409 ...): showing an old copy of a record the
 * person is no longer allowed to see (or that was deleted) is wrong, and for a signed-out session it only hid the real situation.
 */
export function shouldFallBackToCache(err: unknown): boolean {
  if (!(err instanceof HttpErrorResponse)) return true;
  return err.status === 0 || err.status >= 500;
}

export function noCachedCopyError(cacheKey: string, original?: unknown): unknown {
  if (original instanceof HttpErrorResponse && original.status > 0) return original;
  const e = new Error(`No cached copy available (${cacheKey})`);
  (e as any).cause = original;
  return e;
}

// Generic write-through cache for single-record detail views (Transport Unit, Business
// Partner, Product, Loading Site). Whatever a detail fetch successfully returns online is
// stashed under a caller-provided key; re-fetching the same key while offline falls back to
// the last-seen copy instead of a network error. Not a full sync — only records you've
// actually opened while online are available offline.
@Injectable({ providedIn: 'root' })
export class DetailCacheService {
  /** fallback (2026-09-29): offline and no saved copy of this record → a lighter stand-in (e.g. its row from
   *  the full list saved on the phone), so the screen can still open and show the record read-only. */
  cacheThrough<T>(cacheKey: string, fetch: () => Observable<T>, fallback?: () => Promise<T | undefined>): Observable<T> {
    const key = scopedKey(cacheKey);
    return fetch().pipe(
      tap((data) => {
        if (isOfflineCapableNative()) {
          offlineDb.detailCache.put({ key, data, fetchedAt: Date.now() });
        }
      }),
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readCache<T>(cacheKey, fallback, err) : throwError(() => err))),
    );
  }

  private readCache<T>(cacheKey: string, fallback?: () => Promise<T | undefined>, original?: unknown): Observable<T> {
    const key = scopedKey(cacheKey);
    return from(offlineDb.detailCache.get(key)).pipe(
      switchMap((entry) => {
        if (!entry && fallback) {
          return from(fallback().catch(() => undefined)).pipe(switchMap((stand) => stand !== undefined && stand !== null
            ? of(stand)
            : throwError(() => noCachedCopyError(cacheKey, original))));
        }
        if (!entry) return throwError(() => noCachedCopyError(cacheKey, original));
        // A cached file opened from the phone — remember when, so the file clean-up keeps it (Phase 2).
        if (entry.data instanceof Blob) {
          offlineDb.detailCache.update(key, { usedAt: Date.now() }).catch(() => undefined);
        }
        return of(entry.data as T);
      }),
    );
  }
}
